import React, { useState, useEffect } from 'react';
import { GoogleLogin, CredentialResponse, googleLogout } from '@react-oauth/google';
import AppleLogin from 'react-apple-login';
import { Button } from '@/components/ui/button';
import { AppleIcon, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { SITE_URL } from '@/config/api';
import { isGoogleSignInBlocked } from '@/utils/webview-detection';
import {
  isNativeApp,
  shouldShowGoogleButton,
  shouldShowAppleButton,
} from '@/utils/platform';
import { nativeGoogleSignIn, nativeAppleSignIn, ensureSocialLoginInitialized } from '@/utils/nativeAuth';
import GoogleWebviewAuth from '@/components/auth/GoogleWebviewAuth';

const GoogleGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

interface SocialAuthButtonsProps {
  /** When true, render large full-width stacked "app" style buttons. */
  appStyle?: boolean;
  /**
   * When provided, the component does NOT sign the user in itself.
   * It hands the provider id_token back to the parent instead
   * (used by the "add another account" flow).
   */
  onCredential?: (provider: 'google' | 'apple', idToken: string) => void | Promise<void>;
}

/**
 * Unified Google + Apple sign-in buttons.
 * - Native app (Capacitor): uses @capgo/capacitor-social-login for a fully native flow.
 * - Web: uses @react-oauth/google + react-apple-login (with webview fallback).
 * Visibility: Google on any Android/iOS/desktop, Apple only on iOS/iPhone.
 */
const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({ appStyle = false, onCredential }) => {
  const { t } = useLanguage();
  const { loginWithGoogle, loginWithApple } = useAuth();
  const { toast } = useToast();
  const [native, setNative] = useState(isNativeApp());
  const isInBlockingWebview = isGoogleSignInBlocked();
  const [busy, setBusy] = useState<'google' | 'apple' | null>(null);

  useEffect(() => {
    // Méthode 1: User Agent (la plus fiable si configurée côté natif)
    const uaContainsAppIdentifier = /median/i.test(navigator.userAgent);
    if (uaContainsAppIdentifier) {
      setNative(true);
      return;
    }

    // Méthode 2: Pont Capacitor déjà prêt au chargement
    if (isNativeApp()) {
      setNative(true);
      return;
    }

    // Méthode 3: Écouter l'événement `capacitorReady`
    const handleCapacitorReady = () => {
      if (isNativeApp()) setNative(true);
    };
    window.addEventListener('capacitorReady', handleCapacitorReady);

    const timer = setTimeout(() => {
      if (isNativeApp()) setNative(true);
    }, 500);

    return () => {
      window.removeEventListener('capacitorReady', handleCapacitorReady);
      clearTimeout(timer);
    };
  }, []);

  // Pré-initialise le plugin SocialLogin dès que l'environnement natif est détecté
  // pour éviter le blocage de la popup au premier clic.
  useEffect(() => {
    if (native) {
      ensureSocialLoginInitialized();
    }
  }, [native]);
  const [isAppleScriptLoaded, setIsAppleScriptLoaded] = useState(false);

  useEffect(() => {
    // Si le bouton Apple ne doit pas être affiché, ou si on est en natif, on n'a rien à faire.
    if (!shouldShowAppleButton() || isNativeApp()) return;

    // Si le script est déjà là (navigation), on met l'état à jour.
    if (window.AppleID) {
      setIsAppleScriptLoaded(true);
      return;
    }

    // Sinon, on injecte le script.
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => setIsAppleScriptLoaded(true);
    document.head.appendChild(script);

    return () => {
      void document.head.removeChild(script); // Ensure cleanup function returns void
    };
  }, []);

  const showGoogle = shouldShowGoogleButton();
  const showApple = shouldShowAppleButton();

  const handleNativeGoogle = async () => {
    try {
      setBusy('google');
      const idToken = await nativeGoogleSignIn(native);
      if (onCredential) {
        await onCredential('google', idToken);
      } else {
        await loginWithGoogle(idToken);
      }
    } catch (error) {
      console.error('Native Google sign-in error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: error instanceof Error ? error.message : 'Authentication failed',
      });
    } finally {
      setBusy(null);
    }
  };

  const handleNativeApple = async () => {
    try {
      setBusy('apple');
      const idToken = await nativeAppleSignIn(native);
      if (onCredential) {
        await onCredential('apple', idToken);
      } else {
        await loginWithApple(idToken);
      }
    } catch (error) {
      console.error('Native Apple sign-in error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: error instanceof Error ? error.message : 'Authentication failed',
      });
    } finally {
      setBusy(null);
    }
  };

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      if (onCredential) {
        void onCredential('google', credentialResponse.credential);
      } else {
        loginWithGoogle(credentialResponse.credential);
      }
    }
  };

  const handleAppleResponse = (response: any) => {
    if (response.authorization && response.authorization.id_token) {
      if (onCredential) {
        void onCredential('apple', response.authorization.id_token);
      } else {
        loginWithApple(response.authorization.id_token);
      }
    } else if (response.error) {
      console.error('Apple login failed:', response.error);
    }
  };

    const wrapperClass = appStyle
    ? 'flex flex-col gap-3 w-full items-stretch'
    : 'flex flex-row gap-3 justify-center';

  // ---- Native app: fully native buttons ----
  if (native) {
    return (
      <div className={wrapperClass}>
        {showGoogle && (
          <GoogleWebviewAuth onClose={() => {}} />
        )}
        {showApple && isAppleScriptLoaded && (
          <Button
            type="button"
            onClick={handleNativeApple}
            disabled={busy !== null}
            className="w-full h-12 rounded-full bg-black text-white hover:bg-black/90 flex items-center justify-center gap-2 text-base font-medium"
          >
            {busy === 'apple' ? <Loader2 className="h-5 w-5 animate-spin" /> : <AppleIcon className="h-5 w-5" />}
            <span>{t('login.continueWithApple')}</span>
          </Button>
        )}
      </div>
    );
  }

  // ---- Web ----
  /*const wrapperClass = appStyle
    ? 'flex flex-col gap-3 w-full items-stretch'
    : 'flex flex-row gap-3 justify-center';*/

  return (
    <div className={wrapperClass}>
      {showGoogle && (
        <div className={appStyle ? 'w-full' : 'w-auto'}>
          {!isInBlockingWebview ? (
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => console.error('Google login failed')}
              theme="outline"
              shape="pill"
              text="continue_with"
              size="large"
              containerProps={{ style: { minWidth: '220px', display: 'flex', justifyContent: 'center' } }}
            />
          ) : (
            <GoogleWebviewAuth onClose={() => {}} />
          )}
        </div>
      )}
      {showApple && isAppleScriptLoaded && (
        <div className={appStyle ? 'w-full flex justify-center' : 'flex justify-center'}>
          <AppleLogin
            clientId="com.sportlogin.login"
            redirectURI={`${SITE_URL}/apple-auth-done`}
            usePopup={true}
            callback={handleAppleResponse}
            scope="email name"
            responseMode="query"
            render={(renderProps) => (
              <Button
                variant="outline"
                className={
                  appStyle
                    ? 'w-auto h-10 rounded-full bg-black text-white flex items-center justify-center gap-2 text-base font-medium'
                    : 'h-10 px-6 rounded-full flex items-center justify-center gap-2'
                }
                onClick={renderProps.onClick}
              >
                <AppleIcon className="w-5 h-5" />
                <span>{t('login.continueWithApple')}</span>
              </Button>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SocialAuthButtons;
